import { useCallback, useEffect, useState } from 'react'
import React from 'react'
import { FilledButton } from '../../../common/buttons/FilledButton'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { DialogError } from '../../../common/dialogs/DialogError'
import { PaymentMethodCollection } from '../../../payment/payment-method/collection/PaymentMethodCollection'
import ActivityProgression, { ProgressionStatus } from '../../../common/ActivityProgression'
import { useUpdatePaymentMethodFinalization } from '../../../../../hooks/payment-methods/UpdateFinalizationHook'
import { useIntent } from '../../../../../hooks/payment-methods/IntentHook'
import { translations } from '../../../../../utils/translations/translations-manager'

export function ContractPaymentMethodUpdate({
  charityId,
  contractId,
  success,
  complete,
}: {
  charityId: string
  contractId: string
  success: () => void
  complete: () => void
}): JSX.Element {
  const [state, setState] = useState<'intent' | 'payment' | 'finalization' | 'success'>('intent')
  const [error, setError] = useState<unknown | null>(null)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [setupIntentId, setSetupIntentId] = useState<string>()
  const onSuccessfulIntent = useCallback(async (clientSecret: string) => {
    setClientSecret(clientSecret)
    setState('payment')
  }, [])

  const onFailedIntent = useCallback((error: unknown) => {
    setError(error)
  }, [])

  const onSuccessfulCollection = useCallback((setupIntentId: string) => {
    setSetupIntentId(setupIntentId)
    setState('finalization')
  }, [])

  const onFailedCollection = useCallback((error: unknown) => {
    setError(error)
  }, [])

  const onSuccessfulFinalization = useCallback(async () => {
    setState('success')
    complete()
  }, [complete])

  const onFailedFinalization = useCallback((error: unknown) => {
    setError(error)
  }, [])

  if (error) {
    return (
      <DialogError
        message={translations.texts.change_payment_method_error.toString()}
        onRetry={() => {
          setState('intent')
          setError(null)
        }}
        retryTitle={translations.texts.retry.toString()}
      />
    )
  }

  if (state === 'intent') {
    return <ContractPaymentMethodUpdateIntent success={onSuccessfulIntent} failure={onFailedIntent} charityId={charityId} contractId={contractId} />
  } else if (state === 'payment' && clientSecret) {
    return <PaymentMethodCollection success={onSuccessfulCollection} failure={onFailedCollection} clientSecret={clientSecret} />
  } else if (state === 'finalization' && setupIntentId) {
    return (
      <ContractPaymentMethodUpdateFinalization
        charityId={charityId}
        contractId={contractId}
        setupIntent={setupIntentId}
        success={onSuccessfulFinalization}
        failure={onFailedFinalization}
      />
    )
  } else if (state === 'success') {
    return (
      <>
        <div className="flex flex-col justify-center items-center">
          <CheckCircleIcon className="mr-2 mb-2 w-12 h-12 stroke-emerald-500" />
          <p className="mb-5">{translations.texts.change_payment_method_success.toString()}</p>
        </div>
        <div className="flex justify-center">
          <FilledButton onClick={success} label={translations.texts.close.toString()} />
        </div>
      </>
    )
  } else {
    return <></> // TODO: Handle error, this should never happen
  }
}

export function ContractPaymentMethodUpdateFinalization({
  charityId,
  contractId,
  setupIntent,
  success,
  failure,
}: {
  charityId: string
  contractId: string
  setupIntent: string
  success: () => Promise<void>
  failure: (e: unknown) => void
}): JSX.Element {
  const { state, finalize } = useUpdatePaymentMethodFinalization({ charityId, contractId, success, failure })

  useEffect(() => {
    void finalize(setupIntent)
  }, [])
  return (
    <div>
      <ActivityProgression status={getFinalizationProgressionStatus(state)} />
    </div>
  )
}

export function ContractPaymentMethodUpdateIntent({
  success,
  failure,
  charityId,
  contractId,
}: {
  success: (clientSecret: string) => void
  failure: (e: unknown) => void
  charityId: string
  contractId: string
}): JSX.Element {
  const { intent } = useIntent()
  const [state, setState] = useState<'idle' | 'intent' | 'success'>('idle')
  useEffect(() => {
    setState('intent')
    void intent(charityId, contractId)
      .then(clientSecret => {
        setState('success')
        success(clientSecret)
      })
      .catch(failure)
  }, [])
  return (
    <div>
      <ActivityProgression status={getIntentProgressionStatus(state)} />
    </div>
  )
}

function getFinalizationProgressionStatus(state: 'idle' | 'finalize' | 'success'): ProgressionStatus {
  switch (state) {
    case 'idle':
      return { status: 'idle' }
    case 'finalize':
      return { status: 'pending' }
    case 'success':
      return { status: 'success' }
  }
}

function getIntentProgressionStatus(state: 'idle' | 'intent' | 'success'): ProgressionStatus {
  switch (state) {
    case 'idle':
      return { status: 'idle' }
    case 'intent':
      return { status: 'pending' }
    case 'success':
      return { status: 'success' }
  }
}
